import api from './client'

/**
 * Озвучка ElevenLabs (бэк — app/api/v1/voice.py). Синхронно: mp3 уже лежит у
 * нас, audio_url приходит в ответе, трек сразу виден на вкладке «Озвучка».
 */

export interface VoiceInfo {
	id: string
	/** Имя голоса ElevenLabs без описания («Sarah»). */
	name: string
	/** Пол по меткам ElevenLabs; null — не указан. */
	gender: 'm' | 'f' | null
	/** Короткая подпись по-русски («молодой · для рассказов»). */
	hint: string | null
	/** Превью голоса — русское, если ElevenLabs его проверял на русском. */
	preview_url: string | null
	/** Язык голоса библиотеки (ISO 639-1); у стандартных — null (многоязычные). */
	language?: string | null
}

export interface VoicesResponse {
	ok: boolean
	voices?: VoiceInfo[]
	default_voice?: string | null
	error?: string
}

export interface VoiceLanguagesResponse {
	ok: boolean
	/** Языки модели: code — ISO 639-1, name — английское имя от ElevenLabs. */
	languages: { code: string; name: string }[]
	/** Сценарии библиотеки: popular, narration, characters… standard. */
	use_cases: { key: string; label: string }[]
	default_language: string
}

export interface VoiceLibraryResponse {
	ok: boolean
	voices?: VoiceInfo[]
	has_more?: boolean
	error?: string
}

export interface SpeechResponse {
	ok: boolean
	request_id?: string
	audio_url?: string
	credits_spent?: number
	error?: string
}

/** Стандартные голоса ElevenLabs (бэк кэширует на несколько часов). */
export async function getVoices(): Promise<VoicesResponse> {
	return api.request<VoicesResponse>('/api/v1/voice/voices')
}

/** Языки модели и сценарии библиотеки — фильтры выбора голоса. */
export async function getVoiceLanguages(model?: string): Promise<VoiceLanguagesResponse> {
	const q = model ? `?model=${encodeURIComponent(model)}` : ''
	return api.request<VoiceLanguagesResponse>(`/api/v1/voice/languages${q}`)
}

/** Библиотека голосов ElevenLabs по языку и сценарию (как на elevenlabs.io).
 *  use_case='standard' — стандартные голоса ElevenLabs, от языка не зависят. */
export async function getVoiceLibrary(params: { language: string; use_case: string; page: number }): Promise<VoiceLibraryResponse> {
	const q = new URLSearchParams({ language: params.language, use_case: params.use_case, page: String(params.page) })
	return api.request<VoiceLibraryResponse>(`/api/v1/voice/library?${q}`)
}

/** Озвучка одного текста одним голосом. model — id модели каталога (elevenlabs-v4). */
export async function generateSpeech(params: {
	text: string
	voice_id?: string
	model: string
	title?: string
}): Promise<SpeechResponse> {
	return api.request<SpeechResponse>('/api/v1/voice/tts', { method: 'POST', body: params })
}

/** Диалог: реплики разными голосами → один mp3. До 10 реплик и 2000 символов на все. */
export async function generateDialogue(params: {
	lines: { text: string; voice_id: string }[]
	model: string
	title?: string
}): Promise<SpeechResponse> {
	return api.request<SpeechResponse>('/api/v1/voice/dialogue', { method: 'POST', body: params })
}
